import PropTypes from 'prop-types';
import Logger from '@medflex/utils/Logger';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { oidcClientIds } from './GenericUserManager';
import DesktopUserManager from './DesktopUserManager';
import DesktopUserManagerLegacy from './legacy/DesktopUserManagerLegacy';
import LoadingView from '../LoadingView';

const isDesktopLegacyAuthentication = async () => {
  try {
    const isLegacy = await window.electron.authentication.isLegacyAuthentication();
    return !!isLegacy;
  } catch (ex) {
    // Expected behavior for older versions of the desktop app, because it does not implement this function
    Logger.log(`[DesktopUserManagerSwitch] - isLegacy error`, ex);
    return true;
  }
};

const DesktopUserManagerSwitch = ({
  children,
  basePath,
  onSessionLoaded,
  silentRenewDisabled,
  oidcAuthority,
  externalExpired,
  silentRedirectUri,
  clientId,
  postLogoutRedirectUri,
}) => {
  const { t } = useTranslation('components');
  const [isLegacy, setIsLegacy] = useState(null);

  useEffect(() => {
    if (isLegacy === null) {
      (async () => {
        setIsLegacy(await isDesktopLegacyAuthentication());
      })();
    }
  }, [isLegacy]);

  useEffect(() => {
    if (isLegacy !== null) Logger.log(`[DesktopUserManagerSwitch] - isLegacy returned ${isLegacy}`);
  }, [isLegacy]);

  if (isLegacy === null)
    return (
      <LoadingView
        title={t('UserManager.LoadingView.title')}
        message={t('UserManager.LoadingView.message')}
      />
    );

  if (isLegacy)
    return (
      <DesktopUserManagerLegacy onSessionLoaded={onSessionLoaded} externalExpired={externalExpired}>
        {children}
      </DesktopUserManagerLegacy>
    );

  return (
    <DesktopUserManager
      basePath={basePath}
      onSessionLoaded={onSessionLoaded}
      silentRenewDisabled={silentRenewDisabled}
      externalExpired={externalExpired}
      oidcAuthority={oidcAuthority}
      silentRedirectUri={silentRedirectUri}
      clientId={clientId}
      postLogoutRedirectUri={postLogoutRedirectUri}
    >
      {children}
    </DesktopUserManager>
  );
};

// ######################################################################################
// ## PropTypes
// ######################################################################################

DesktopUserManagerSwitch.propTypes = {
  children: PropTypes.node.isRequired,
  basePath: PropTypes.string.isRequired,
  onSessionLoaded: PropTypes.func,
  oidcAuthority: PropTypes.string.isRequired,
  silentRenewDisabled: PropTypes.bool,
  externalExpired: PropTypes.bool,
  clientId: PropTypes.oneOf(Object.values(oidcClientIds)).isRequired,
  postLogoutRedirectUri: PropTypes.string.isRequired,
  silentRedirectUri: PropTypes.string.isRequired,
};

DesktopUserManagerSwitch.defaultProps = {
  onSessionLoaded: () => {},
  silentRenewDisabled: false,
  externalExpired: false,
};

export default DesktopUserManagerSwitch;
